import React, { useCallback, useMemo, useState } from 'react';
import { FileText, Plus, Wand2, Trash2, ChevronRight, ChevronDown } from 'lucide-react';
import { useBoqProjectItems } from '../hooks/useBoqProjectItems';
import { useBoqLibrary } from '../hooks/useBoqLibrary';
import { useBoqClauses, CLAUSE_KINDS, CLAUSE_LABEL } from '../hooks/useBoqClauses';
import { useBoqBillAdjustments, computeBuildUp } from '../hooks/useBoqBillAdjustments';
import { useBoqScopeParties } from '../hooks/useBoqScopeParties';
import { useBoqTaxonomies } from '../hooks/useBoqTaxonomies';
import { BrioCard, BrioButton, BrioEmptyState, BrioInput, BrioSelect } from '../../components/BrioComponents';
import { formatMoney, formatQty } from '../utils/money';
import { canonicalTradesFor } from '../utils/canonicalTrades';
import { renderSummaryHtml, summaryRowsForDetail, openBillForPrint } from '../utils/renderBillHtml';
import { ToolbarIcon } from './ToolbarIcon';
import { StickyScrollBox } from './StickyScrollbar';

const FT2_PER_M2 = 10.7639;

// True lump-sum line types: summed separately and excluded from cost-per-area.
// SD area-rate "allowance" lines are NOT lumps — they're priced works and get
// broken down in the grouped table.
const isLumpType = (it) => it.lineType === 'provisional_sum' || it.lineType === 'pc_sum' || it.lineType === 'dayworks';

function buildGroups(items, mode, zonesById, scopeById, tradeGroupByItem, chapterByItem, subtradeByItem, alternatesByGroup, currency, canonicalTrades) {
  if (mode === 'trade') return buildTradeRows(items, tradeGroupByItem, chapterByItem, subtradeByItem, alternatesByGroup, currency, canonicalTrades);
  const map = new Map();
  items.forEach((it) => {
    let key; let label;
    if (mode === 'zone') {
      const z = it.projectZoneId ? zonesById.get(it.projectZoneId) : null;
      key = it.projectZoneId || '—';
      label = z ? `${z.code} · ${z.label}` : 'Unzoned';
    } else { // responsibility
      const party = it.scopePartyId ? scopeById.get(it.scopePartyId) : null;
      key = it.scopePartyId || '__none__';
      label = party ? (party.isExternal ? `${party.name} (external)` : party.name) : 'Unassigned (our scope)';
    }
    if (!map.has(key)) map.set(key, { key, label, quoted: 0, billed: 0, lines: 0, depth: 0, isSection: false });
    const g = map.get(key);
    g.quoted += Number(it.amountQuoted || 0);
    g.billed += Number(it.amountBilled || 0);
    g.lines += 1;
  });
  return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
}

// Trade view, two levels: SD allowance lines roll up Section (trade) -> Sub-trade
// or option-group; measured lines roll up to their trade_group (single level).
// Returns a flat, render-ordered list — section rows carry depth 0 (isSection
// true when they have sub-rows beneath), sub-trade rows carry depth 1. Order
// follows the taxonomy sort for measured lines and the bill's line order
// (sequence_order) for allowances.
//
// Sub-rows for allowance lines are created when EITHER the line has an
// option_group (so the chosen alternate surfaces as its own row labelled with
// the option-group name plus the selected description) OR a real subtrade
// distinct from the trade. Lines with no option_group and no real subtrade
// contribute only to the section total — no phantom "same as trade" sub-row.
function formatRateUnit(line, currency) {
  const rate = line.rateQuoted;
  const unit = line.unit || line.version?.unit || '';
  if (rate == null) return null;
  return `${formatMoney(rate, currency)}${unit ? `/${unit}` : ''}`;
}

function buildTradeRows(items, tradeGroupByItem, chapterByItem, subtradeByItem, alternatesByGroup, currency, canonicalTrades) {
  const alts = alternatesByGroup || new Map();
  const sections = new Map();

  // Canonical mode: when a canonicalTrades list is supplied (the 11
  // brio_trade top-level chapters in sort order), every chapter is rendered
  // even if a project has no items in it — so the user sees what's missing.
  // Section labels are then numbered "1. ", "2. ", … per canonical position.
  const canonByLabel = new Map();
  if (canonicalTrades && canonicalTrades.length > 0) {
    canonicalTrades.forEach((t, i) => {
      const key = `t:${t.label}`;
      canonByLabel.set(t.label, { sort: i + 1, num: i + 1 });
      sections.set(key, {
        key,
        label: t.label,
        sort: i + 1,
        canonNum: i + 1,
        quoted: 0,
        billed: 0,
        subs: new Map(),
        directLines: [],
      });
    });
  }

  items.forEach((it) => {
    const q = Number(it.amountQuoted || 0); const b = Number(it.amountBilled || 0);
    const seq = it.sequenceOrder ?? Number.MAX_SAFE_INTEGER;
    let secKey; let secLabel; let secSort; let subKey = null; let subLabel = null; let isOption = false; let optionGroup = null;
    const tg = tradeGroupByItem.get(it.version?.itemCode) || null;
    // Roll fine library trades up to the canonical chapter so tender bills
    // organise onto the same 11 chapters as SD; SD lines store the chapter and
    // pass straight through. The fine trade is kept as the sub-trade row.
    const chapter = (it.trade && canonByLabel.has(it.trade))
      ? it.trade
      : (chapterByItem ? chapterByItem.get(it.version?.itemCode) : null);
    // Canonical brio sub-trade for this line (library bills), else the coarser
    // trade_group label — so SD and TD show the same sub-trade names.
    const brioSub = subtradeByItem ? subtradeByItem.get(it.version?.itemCode) : null;
    if (it.lineType === 'allowance') {
      secLabel = chapter || it.trade || 'Other'; secKey = `t:${secLabel}`;
      // In canonical mode, lock sort to the canonical position so chapters
      // stay in 1–11 order regardless of bill insertion order.
      const canon = canonByLabel.get(secLabel);
      secSort = canon ? canon.sort : seq;
      if (it.optionGroup) {
        const chosen = it.descriptionOverride || it.version?.shortDescription || '';
        subLabel = chosen ? `${it.optionGroup} → ${chosen}` : it.optionGroup;
        subKey = `o:${secLabel}/${it.optionGroup}`;
        isOption = true; optionGroup = it.optionGroup;
      } else if (it.subtrade && it.subtrade !== it.trade) {
        subLabel = it.subtrade;
        subKey = `s:${secLabel}/${subLabel}`;
      } else if (chapter && (brioSub || (tg && tg.label))) {
        const s = brioSub || tg.label;
        if (s !== secLabel) { subLabel = s; subKey = `s:${secLabel}/${s}`; }
      }
    } else if (chapter) {
      secLabel = chapter; secKey = `t:${chapter}`;
      const canon = canonByLabel.get(chapter);
      secSort = canon ? canon.sort : (tg?.sortOrder ?? Number.MAX_SAFE_INTEGER);
      const s = brioSub || (tg && tg.label) || null;
      if (s) { subLabel = s; subKey = `s:${chapter}/${s}`; }
    } else if (tg) {
      secKey = `g:${tg.code}`; secLabel = tg.label; secSort = tg.sortOrder ?? Number.MAX_SAFE_INTEGER;
    } else {
      const p = (it.version?.fullCode || '').slice(0, 2) || 'Other';
      secKey = `p:${p}`; secLabel = p; secSort = Number.MAX_SAFE_INTEGER;
    }
    if (!sections.has(secKey)) sections.set(secKey, { key: secKey, label: secLabel, sort: secSort, quoted: 0, billed: 0, subs: new Map(), directLines: [] });
    const sec = sections.get(secKey);
    sec.quoted += q; sec.billed += b;
    if (secSort < sec.sort) sec.sort = secSort;
    // The individual priced line — the 3rd tier the Summary can expand to.
    const lineRow = {
      id: it.id,
      label: it.descriptionOverride || it.version?.shortDescription || it.subtrade || it.trade || '(item)',
      quoted: q, billed: b, note: formatRateUnit(it, currency),
    };
    if (subKey) {
      if (!sec.subs.has(subKey)) sec.subs.set(subKey, {
        key: subKey, label: subLabel, sort: seq, quoted: 0, billed: 0,
        note: null, isOption, optionGroup, selectedRateNote: null, lines: [],
      });
      const sub = sec.subs.get(subKey);
      sub.quoted += q; sub.billed += b;
      if (isOption && !sub.selectedRateNote) sub.selectedRateNote = formatRateUnit(it, currency);
      if (seq < sub.sort) sub.sort = seq;
      // Option-group sub-rows already break down via their alternates; for all
      // other sub-trades, keep the constituent lines for the expandable 3rd tier.
      if (!isOption) sub.lines.push(lineRow);
    } else {
      sec.directLines.push(lineRow);
    }
  });
  const bySort = (a, b) => (a.sort !== b.sort ? a.sort - b.sort : a.label.localeCompare(b.label));
  const rows = [];
  // In canonical mode the section label is prefixed with its 1–11 chapter
  // number so the QS can see at a glance that the whole catalogue is present.
  const sectionLabel = (sec) =>
    (sec.canonNum ? `${sec.canonNum}. ${sec.label}` : sec.label);
  // Emit line rows for a sub-trade / section bucket — each constituent item,
  // indented one level deeper, flagged isLine so the UI shows them only when the
  // parent is expanded.
  const pushLines = (lines, parentKey, depth) => {
    lines.forEach((ln) => rows.push({
      key: `ln:${ln.id}`, label: ln.label, note: ln.note,
      quoted: ln.quoted, billed: ln.billed, depth, isLine: true, parentKey,
    }));
  };
  [...sections.values()].sort(bySort).forEach((sec) => {
    const subs = [...sec.subs.values()].sort(bySort);
    if (subs.length > 0 || sec.directLines.length > 0) {
      const secRow = { key: sec.key, label: sectionLabel(sec), quoted: sec.quoted, billed: sec.billed, depth: 0, isSection: true };
      if (sec.directLines.length > 0) { secRow.expandable = true; secRow.rowKey = sec.key; secRow.count = sec.directLines.length; }
      rows.push(secRow);
      // Chapter-direct lines (items with no sub-trade).
      pushLines(sec.directLines, sec.key, 1);
      subs.forEach((s) => {
        // Selected option / sub-trade row — expandable to its line items.
        const subRow = {
          key: s.key, label: s.label, note: s.selectedRateNote || null,
          quoted: s.quoted, billed: s.billed, depth: 1, isSection: false,
        };
        if (s.lines && s.lines.length > 0) { subRow.expandable = true; subRow.rowKey = s.key; subRow.count = s.lines.length; }
        rows.push(subRow);
        pushLines(s.lines || [], s.key, 2);
        // Each unselected alternate is its own faded row beneath, with rate and amount visible.
        if (s.isOption && s.optionGroup) {
          const groupAlts = alts.get(s.optionGroup) || [];
          groupAlts.forEach((alt) => {
            const altDesc = alt.descriptionOverride || alt.version?.shortDescription || '(unnamed)';
            rows.push({
              key: `alt:${alt.id}`,
              label: `↳ ${altDesc}`,
              note: formatRateUnit(alt, currency),
              quoted: Number(alt.amountQuoted || 0),
              billed: Number(alt.amountBilled || 0),
              depth: 2,
              isSection: false,
              isAlternate: true,
            });
          });
        }
      });
    } else if (sec.canonNum) {
      // Canonical chapter with no items — still render the header (numbered)
      // so the QS can see what's missing.
      rows.push({
        key: sec.key,
        label: sectionLabel(sec),
        quoted: 0,
        billed: 0,
        depth: 0,
        isSection: true,
        isEmpty: true,
      });
    } else {
      rows.push({ key: sec.key, label: sectionLabel(sec), quoted: sec.quoted, billed: sec.billed, depth: 0, isSection: false });
    }
  });
  return rows;
}

function ClauseSection({ kind, clauses, library, onAdd, onAddFromLibrary, onUpdate, onDelete }) {
  const mine = clauses.filter((c) => c.kind === kind).sort((a, b) => a.sortOrder - b.sortOrder);
  const usedTexts = new Set(mine.map((c) => c.text));
  const suggestions = library.filter((l) => l.kind === kind && !usedTexts.has(l.text));
  return (
    <div style={{ marginBottom: '1rem' }}>
      <h4 style={{ margin: '0 0 0.4rem', fontFamily: 'Raleway, sans-serif' }}>{CLAUSE_LABEL[kind]}</h4>
      {mine.length === 0 && <div style={{ fontSize: '0.82rem', color: 'var(--brio-text-muted)', marginBottom: '0.4rem' }}>None yet.</div>}
      {mine.map((c) => (
        <div key={c.id} style={{ display: 'flex', gap: '0.4rem', alignItems: 'flex-start', marginBottom: '0.3rem' }}>
          <textarea defaultValue={c.text}
            onBlur={(e) => { if (e.target.value.trim() && e.target.value !== c.text) onUpdate(c.id, e.target.value); }}
            rows={1}
            style={{ flex: 1, fontSize: '0.85rem', padding: '0.3rem 0.5rem', border: '1px solid var(--brio-border, #ccc)', borderRadius: 4, fontFamily: 'inherit', resize: 'vertical' }} />
          <ToolbarIcon onClick={() => onDelete(c.id)} danger tooltip="Remove clause">
            <Trash2 size={14} />
          </ToolbarIcon>
        </div>
      ))}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.35rem' }}>
        {suggestions.map((s) => (
          <button key={s.id} type="button" onClick={() => onAddFromLibrary(s)} title="Add from library"
            style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', border: '1px dashed var(--brio-border, #ccc)', borderRadius: 12, background: 'transparent', cursor: 'pointer', color: 'var(--brio-text-muted)' }}>
            + {s.text.length > 48 ? s.text.slice(0, 46) + '…' : s.text}
          </button>
        ))}
        <button type="button" onClick={() => onAdd(kind)}
          style={{ fontSize: '0.75rem', padding: '0.2rem 0.6rem', border: '1px solid var(--brio-border, #ccc)', borderRadius: 4, background: 'transparent', cursor: 'pointer' }}>
          + Custom
        </button>
      </div>
    </div>
  );
}

function BuildUpCard({ projApi, netQuoted, netBilled, currency }) {
  const { adjustments, addAdjustment, updateAdjustment, deleteAdjustment, applyDefaults } = projApi;
  const { rows, grandQuoted, grandBilled } = computeBuildUp(netQuoted, netBilled, adjustments);
  return (
    <BrioCard>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h3 style={{ margin: 0, fontFamily: 'Raleway, sans-serif', fontSize: '1.05rem' }}>Bill build-up</h3>
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
          {adjustments.length === 0 && (
            <BrioButton variant="secondary" onClick={() => applyDefaults(currency)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.7rem', fontSize: '0.82rem' }}>
              <Wand2 size={14} /> Apply standard build-up
            </BrioButton>
          )}
          <BrioButton variant="secondary" onClick={() => addAdjustment({ label: 'Adjustment', value: 0, valueType: 'percent', base: 'running', sign: 'add' })}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.7rem', fontSize: '0.82rem' }}>
            <Plus size={14} /> Add row
          </BrioButton>
        </div>
      </div>
      <StickyScrollBox style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--brio-border, #ddd)', color: 'var(--brio-text-muted)' }}>
              <th style={{ padding: '0.4rem' }}>Item</th>
              <th style={{ padding: '0.4rem' }}>+/−</th>
              <th style={{ padding: '0.4rem', textAlign: 'right' }}>Value</th>
              <th style={{ padding: '0.4rem' }}>Type</th>
              <th style={{ padding: '0.4rem' }}>On</th>
              <th style={{ padding: '0.4rem', textAlign: 'right' }}>Estimate</th>
              <th style={{ padding: '0.4rem', textAlign: 'right' }}>Tender</th>
              <th style={{ padding: '0.4rem' }} />
            </tr>
          </thead>
          <tbody>
            <tr style={{ fontWeight: 600, borderBottom: '1px solid var(--brio-border-subtle, #eee)' }}>
              <td style={{ padding: '0.4rem' }} colSpan={5}>Net works</td>
              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{formatMoney(netQuoted, currency)}</td>
              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{formatMoney(netBilled, currency)}</td>
              <td />
            </tr>
            {rows.map((r) => (
              <tr key={r.id} style={{ borderBottom: '1px solid var(--brio-border-subtle, #eee)' }}>
                <td style={{ padding: '0.2rem' }}>
                  <BrioInput defaultValue={r.label} onBlur={(e) => e.target.value !== r.label && updateAdjustment(r.id, { label: e.target.value })} style={{ minWidth: 140 }} />
                </td>
                <td style={{ padding: '0.2rem' }}>
                  <BrioSelect value={r.sign} onChange={(e) => updateAdjustment(r.id, { sign: e.target.value })}>
                    <option value="add">+ add</option><option value="deduct">− deduct</option>
                  </BrioSelect>
                </td>
                <td style={{ padding: '0.2rem', textAlign: 'right' }}>
                  <input type="number" step="any" defaultValue={r.value} onBlur={(e) => Number(e.target.value) !== r.value && updateAdjustment(r.id, { value: e.target.value })}
                    style={{ width: 70, textAlign: 'right', padding: '0.2rem 0.3rem', border: '1px solid var(--brio-border, #ccc)', borderRadius: 4 }} />
                </td>
                <td style={{ padding: '0.2rem' }}>
                  <BrioSelect value={r.valueType} onChange={(e) => updateAdjustment(r.id, { valueType: e.target.value })}>
                    <option value="percent">%</option><option value="amount">amount</option>
                  </BrioSelect>
                </td>
                <td style={{ padding: '0.2rem' }}>
                  <BrioSelect value={r.base} onChange={(e) => updateAdjustment(r.id, { base: e.target.value })} disabled={r.valueType === 'amount'}>
                    <option value="net">net works</option><option value="running">running</option>
                  </BrioSelect>
                </td>
                <td style={{ padding: '0.4rem', textAlign: 'right' }}>{formatMoney(r.runningQuoted, currency)}</td>
                <td style={{ padding: '0.4rem', textAlign: 'right' }}>{formatMoney(r.runningBilled, currency)}</td>
                <td style={{ padding: '0.2rem', textAlign: 'right' }}>
                  <ToolbarIcon onClick={() => deleteAdjustment(r.id)} danger tooltip="Remove row">
                    <Trash2 size={14} />
                  </ToolbarIcon>
                </td>
              </tr>
            ))}
            <tr style={{ fontWeight: 700, borderTop: '2px solid var(--brio-gold, #9F902A)' }}>
              <td style={{ padding: '0.4rem' }} colSpan={5}>Grand total</td>
              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{formatMoney(grandQuoted, currency)}</td>
              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{formatMoney(grandBilled, currency)}</td>
              <td />
            </tr>
          </tbody>
        </table>
      </StickyScrollBox>
    </BrioCard>
  );
}

function SummaryTab({ project, zoneTree }) {
  const { items: allItems, loading } = useBoqProjectItems(project.boqId);
  const { tradeGroupByItem, chapterByItem, subtradeByItem } = useBoqLibrary();
  const clausesApi = useBoqClauses(project.boqId);
  const adjApi = useBoqBillAdjustments(project.boqId);
  const scopeApi = useBoqScopeParties(project.boqId);
  const [mode, setMode] = useState('trade');
  const [unit, setUnit] = useState('m2');
  // What the exported summary statement carries: 'headers' = the gold section
  // rows only (a one-page chapter statement), 'details' = those plus the white
  // sub-trade / alternate rows beneath them.
  const [exportDetail, setExportDetail] = useState('details');
  const [excludeExternal, setExcludeExternal] = useState(false);
  // Expanded sub-trade / chapter keys — drill down to individual line items.
  const [expandedRows, setExpandedRows] = useState(() => new Set());
  const toggleRow = useCallback((key) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const scopeById = useMemo(() => {
    const m = new Map();
    scopeApi.parties.forEach((p) => m.set(p.id, p));
    return m;
  }, [scopeApi.parties]);
  const hasExternal = useMemo(() => scopeApi.parties.some((p) => p.isExternal), [scopeApi.parties]);
  const isExternalLine = useCallback(
    (it) => !!(it.scopePartyId && scopeById.get(it.scopePartyId)?.isExternal),
    [scopeById]
  );
  const externalQuoted = useMemo(
    () => allItems.filter(isExternalLine).reduce((a, it) => a + Number(it.amountQuoted || 0), 0),
    [allItems, isExternalLine]
  );
  // Unselected alternates (lines in an option_group that aren't the chosen one)
  // never feed totals / groupings; they're informational only.
  const selectedItems = useMemo(
    () => allItems.filter((it) => it.isSelected !== false),
    [allItems]
  );
  const alternateQuoted = useMemo(
    () => allItems.reduce((a, it) => a + ((it.optionGroup && it.isSelected === false) ? Number(it.amountQuoted || 0) : 0), 0),
    [allItems]
  );
  const hasAlternates = useMemo(() => allItems.some((it) => it.optionGroup && it.isSelected === false), [allItems]);
  // Index the unselected alternates by their option_group so buildTradeRows
  // can decorate each option-group sub-row with the alternates' rates.
  const alternatesByGroup = useMemo(() => {
    const m = new Map();
    allItems.forEach((it) => {
      if (it.optionGroup && it.isSelected === false) {
        if (!m.has(it.optionGroup)) m.set(it.optionGroup, []);
        m.get(it.optionGroup).push(it);
      }
    });
    // Stable order: by sequence then by description.
    m.forEach((list) => list.sort((a, b) =>
      (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0)
      || (a.descriptionOverride || '').localeCompare(b.descriptionOverride || '')));
    return m;
  }, [allItems]);
  // Totals exclude external-party scope when the toggle is on.
  const items = useMemo(
    () => (excludeExternal ? selectedItems.filter((it) => !isExternalLine(it)) : selectedItems),
    [selectedItems, excludeExternal, isExternalLine]
  );

  const cur = project.currency || 'INR';
  const unitLabel = unit === 'ft2' ? 'ft²' : 'm²';
  const toUnit = (m2) => (unit === 'ft2' ? m2 * FT2_PER_M2 : m2);

  const AREA_FIELD = { bua: 'builtUpArea', carpet: 'carpetArea', construction: 'constructionArea' };
  const AREA_LABEL = { bua: 'Built-up', carpet: 'Carpet', construction: 'Construction' };
  const projArea = { bua: project.builtUpArea, carpet: project.carpetArea, construction: project.constructionArea };

  const zonesById = useMemo(() => {
    const m = new Map();
    const walk = (nodes) => nodes.forEach((z) => { m.set(z.id, z); if (z.children) walk(z.children); });
    walk(zoneTree || []);
    return m;
  }, [zoneTree]);
  const zoneList = useMemo(() => [...zonesById.values()], [zonesById]);
  const zoneSum = (key) => zoneList.reduce((s, z) => s + (z[AREA_FIELD[key]] || 0), 0);

  // Measured take-off lines AND SD area-rate allowance lines feed the grouped
  // table + cost/area; only true lump sums (provisional / PC / dayworks) are
  // summed separately (excluded from cost per area).
  const measuredItems = useMemo(() => items.filter((it) => !isLumpType(it)), [items]);
  const lumpItems = useMemo(() => items.filter(isLumpType), [items]);
  const provisionalQuoted = lumpItems.reduce((a, it) => a + Number(it.amountQuoted || 0), 0);
  const provisionalBilled = lumpItems.reduce((a, it) => a + Number(it.amountBilled || 0), 0);
  // Label the grouped subtotal "Works" when it contains SD allowances, else
  // "Measured works".
  const hasAllowance = useMemo(() => measuredItems.some((it) => it.lineType === 'allowance'), [measuredItems]);
  const worksLabel = hasAllowance ? 'Works' : 'Measured works';

  // Canonical 11-trade scaffolding for SD and TD bills: always render all
  // chapters, numbered 1–11 in brio_trade taxonomy order, even when a project
  // has zero lines for some of them. The Tender (TD) stage follows the SD
  // template. Disabled for DD/GFC (those work at item granularity and don't fit
  // the chapter model).
  const { taxonomies } = useBoqTaxonomies();
  const canonicalTrades = useMemo(
    () => canonicalTradesFor(taxonomies, project.schemeStage),
    [taxonomies, project.schemeStage]
  );

  const groups = useMemo(
    () => buildGroups(measuredItems, mode, zonesById, scopeById, tradeGroupByItem, chapterByItem, subtradeByItem, alternatesByGroup, cur, canonicalTrades),
    [measuredItems, mode, zonesById, scopeById, tradeGroupByItem, chapterByItem, subtradeByItem, alternatesByGroup, cur, canonicalTrades]
  );
  // Only the two-level trade view has white sub-rows under the gold headers,
  // so the export-detail choice is offered only when there is something to drop.
  const hasSubRows = useMemo(() => groups.some((g) => !g.isLine && (g.depth || 0) > 0), [groups]);
  // Sum the lines directly — `groups` may carry section subtotals AND their
  // sub-rows (two-level trade view), so reducing over it would double-count.
  const measuredQuoted = measuredItems.reduce((a, it) => a + Number(it.amountQuoted || 0), 0);
  const measuredBilled = measuredItems.reduce((a, it) => a + Number(it.amountBilled || 0), 0);
  const grandQuoted = measuredQuoted + provisionalQuoted;
  const grandBilled = measuredBilled + provisionalBilled;

  // An area basis is available in trade view if the project has it; in zone
  // view if any zone has it.
  const availableKeys = ['bua', 'carpet', 'construction'].filter((k) => (mode === 'zone' ? zoneSum(k) > 0 : projArea[k] > 0));
  const [selectedKeys, setSelectedKeys] = useState(null);
  const selected = selectedKeys || new Set(['bua', 'carpet', 'construction']);
  const toggleArea = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key); else next.add(key);
    setSelectedKeys(next);
  };
  const activeKeys = availableKeys.filter((k) => selected.has(k));

  // Trade view divides each row by the project area (additive elemental plan);
  // zone view divides each zone by its own area (true density), so the grand
  // is an area-weighted average over Σ(zone areas).
  const groupAreaM2 = (key, group) => (mode === 'zone' ? (zonesById.get(group.key)?.[AREA_FIELD[key]] || 0) : (projArea[key] || 0));
  const grandAreaM2 = (key) => (mode === 'zone' ? zoneSum(key) : (projArea[key] || 0));
  const ratePerArea = (amount, areaM2) => (areaM2 > 0 ? amount / toUnit(areaM2) : null);

  const handleSummaryPdf = () => {
    const rateHeaders = activeKeys.map((k) => `${cur === 'INR' ? '₹' : ''}/${unitLabel} · ${AREA_LABEL[k]}`);
    const pdfGroups = summaryRowsForDetail(groups, exportDetail).map((g) => ({
      label: g.label, note: g.note || null, quoted: g.quoted, billed: g.billed, depth: g.depth || 0,
      isSection: !!g.isSection, isAlternate: !!g.isAlternate,
      rates: activeKeys.map((k) => (g.isAlternate ? null : ratePerArea(g.quoted, groupAreaM2(k, g)))),
    }));
    const measuredRates = activeKeys.map((k) => ratePerArea(measuredQuoted, grandAreaM2(k)));
    const build = computeBuildUp(grandQuoted, grandBilled, adjApi.adjustments);
    const html = renderSummaryHtml({
      projectName: project.name, projectCode: project.projectCode, currency: cur,
      groupMode: mode, detailLevel: exportDetail, groups: pdfGroups, worksLabel,
      measuredQuoted, measuredBilled, provisionalQuoted, provisionalBilled, grandQuoted, grandBilled,
      rateHeaders, measuredRates, grandRateNote: [mode === 'zone' ? 'weighted avg' : '', excludeExternal ? 'excludes external scope' : ''].filter(Boolean).join('; '),
      buildUp: adjApi.adjustments.length ? { rows: build.rows, grandQuoted: build.grandQuoted, grandBilled: build.grandBilled } : null,
      clauses: clausesApi.clauses,
    });
    if (!openBillForPrint(html)) {
      // eslint-disable-next-line no-alert
      window.alert('Allow pop-ups to open the printable summary.');
    }
  };

  if (!project.boqId) {
    return <BrioCard><BrioEmptyState>Set up BOQ for this project (add a line or zone in the Bill tab) to build summaries.</BrioEmptyState></BrioCard>;
  }

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      <BrioCard>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h3 style={{ margin: 0, fontFamily: 'Raleway, sans-serif', fontSize: '1.05rem' }}>Cost summary</h3>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'inline-flex', border: '1px solid var(--brio-border, #ccc)', borderRadius: 4, overflow: 'hidden' }}>
              {[['trade', 'By trade'], ['zone', 'By zone'], ['responsibility', 'By responsibility']].map(([v, label]) => (
                <button key={v} type="button" onClick={() => setMode(v)}
                  style={{ fontSize: '0.78rem', padding: '0.3rem 0.7rem', border: 'none', cursor: 'pointer', background: mode === v ? 'var(--brio-gold, #9F902A)' : 'transparent', color: mode === v ? '#fff' : 'var(--brio-text, #333)' }}>
                  {label}
                </button>
              ))}
            </div>
            <div style={{ display: 'inline-flex', border: '1px solid var(--brio-border, #ccc)', borderRadius: 4, overflow: 'hidden' }}>
              {[['m2', 'per m²'], ['ft2', 'per ft²']].map(([v, label]) => (
                <button key={v} type="button" onClick={() => setUnit(v)}
                  style={{ fontSize: '0.78rem', padding: '0.3rem 0.7rem', border: 'none', cursor: 'pointer', background: unit === v ? 'var(--brio-gold, #9F902A)' : 'transparent', color: unit === v ? '#fff' : 'var(--brio-text, #333)' }}>
                  {label}
                </button>
              ))}
            </div>
            {hasSubRows && (
              <div style={{ display: 'inline-flex', border: '1px solid var(--brio-border, #ccc)', borderRadius: 4, overflow: 'hidden' }}
                title="What the exported summary statement includes">
                {[['headers', 'Headers only'], ['details', 'Headers + details']].map(([v, label]) => (
                  <button key={v} type="button" onClick={() => setExportDetail(v)}
                    style={{ fontSize: '0.78rem', padding: '0.3rem 0.7rem', border: 'none', cursor: 'pointer', background: exportDetail === v ? 'var(--brio-gold, #9F902A)' : 'transparent', color: exportDetail === v ? '#fff' : 'var(--brio-text, #333)' }}>
                    {label}
                  </button>
                ))}
              </div>
            )}
            <BrioButton variant="primary" onClick={handleSummaryPdf} disabled={items.length === 0}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.7rem', fontSize: '0.82rem' }}>
              <FileText size={14} /> Export summary PDF
            </BrioButton>
          </div>
        </div>

        {hasExternal && (
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '0.6rem', fontSize: '0.82rem', color: 'var(--brio-text-muted)', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <input type="checkbox" checked={excludeExternal} onChange={(e) => setExcludeExternal(e.target.checked)} />
              Exclude external scope from totals
            </label>
            <span>External scope: <strong style={{ color: 'var(--brio-text, #333)' }}>{formatMoney(externalQuoted, cur)}</strong> {excludeExternal ? '(excluded below)' : '(included below)'}</span>
          </div>
        )}

        {hasAlternates && (
          <div style={{ marginBottom: '0.6rem', fontSize: '0.82rem', color: 'var(--brio-text-muted)' }}>
            Unselected option alternates total <strong style={{ color: 'var(--brio-text, #333)' }}>{formatMoney(alternateQuoted, cur)}</strong> (excluded — switch the selected option in the Bill tab to change).
          </div>
        )}

        {availableKeys.length > 0 && (
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '0.6rem', fontSize: '0.82rem', color: 'var(--brio-text-muted)', flexWrap: 'wrap' }}>
            <span>Show rate per:</span>
            {availableKeys.map((k) => (
              <label key={k} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <input type="checkbox" checked={selected.has(k)} onChange={() => toggleArea(k)} />
                {AREA_LABEL[k]} ({mode === 'zone' ? 'Σ ' : ''}{formatQty(toUnit(grandAreaM2(k)))} {unitLabel})
              </label>
            ))}
          </div>
        )}

        {loading ? (
          <div style={{ color: 'var(--brio-text-muted)' }}>Loading…</div>
        ) : items.length === 0 ? (
          <BrioEmptyState>No priced lines yet.</BrioEmptyState>
        ) : (
          <StickyScrollBox style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--brio-border, #ddd)' }}>
                  <th style={{ padding: '0.5rem' }}>{mode === 'zone' ? 'Zone' : mode === 'responsibility' ? 'Responsibility' : 'Trade'}</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Estimate</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>% </th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Tender</th>
                  {activeKeys.map((k) => (
                    <th key={k} style={{ padding: '0.5rem', textAlign: 'right' }}>{`${cur === 'INR' ? '₹' : ''}/${unitLabel} · ${AREA_LABEL[k]}`}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groups.filter((g) => !g.isLine || expandedRows.has(g.parentKey)).map((g) => {
                  const expanded = g.expandable && expandedRows.has(g.rowKey);
                  return (
                  <tr key={g.key}
                    onClick={g.expandable ? () => toggleRow(g.rowKey) : undefined}
                    style={{
                    borderBottom: '1px solid var(--brio-border-subtle, #eee)',
                    background: g.isSection ? 'var(--brio-gold-faint, #f7f1d4)' : (g.isAlternate || g.isLine ? '#fafafa' : undefined),
                    color: g.isAlternate || g.isLine ? 'var(--brio-text-muted)' : (g.isEmpty ? 'var(--brio-text-muted)' : undefined),
                    fontStyle: g.isAlternate ? 'italic' : undefined,
                    opacity: g.isEmpty ? 0.75 : undefined,
                    cursor: g.expandable ? 'pointer' : undefined,
                  }}>
                    <td style={{ padding: '0.5rem', paddingLeft: `${0.5 + (g.depth || 0) * 1.25}rem`, fontWeight: g.isSection ? 700 : (g.isAlternate || g.isLine ? 400 : 500) }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {g.expandable && (expanded
                          ? <ChevronDown size={13} style={{ flexShrink: 0, opacity: 0.7 }} />
                          : <ChevronRight size={13} style={{ flexShrink: 0, opacity: 0.7 }} />)}
                        <span>{g.label}{g.isEmpty ? ' — (no items yet)' : ''}{g.expandable ? ` (${g.count})` : ''}</span>
                      </div>
                      {g.note && (
                        <div style={{ fontSize: '0.74rem', fontWeight: 400, color: 'var(--brio-text-muted)', marginTop: 2 }}>
                          {g.note}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: g.isSection ? 700 : 400 }}>{g.isEmpty ? '—' : formatMoney(g.quoted, cur)}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', color: 'var(--brio-text-muted)' }}>{g.isAlternate || g.isEmpty || g.isLine ? '—' : (measuredQuoted ? ((g.quoted / measuredQuoted) * 100).toFixed(1) + '%' : '—')}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{g.isEmpty ? '—' : formatMoney(g.billed, cur)}</td>
                    {activeKeys.map((k) => (
                      <td key={k} style={{ padding: '0.5rem', textAlign: 'right' }}>
                        {g.isAlternate || g.isEmpty || g.isLine ? '' : formatMoney(ratePerArea(g.quoted, groupAreaM2(k, g)), cur)}
                      </td>
                    ))}
                  </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '1px solid var(--brio-border, #ddd)', fontWeight: 600 }}>
                  <td style={{ padding: '0.5rem' }}>{worksLabel}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatMoney(measuredQuoted, cur)}</td>
                  <td />
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatMoney(measuredBilled, cur)}</td>
                  {activeKeys.map((k) => (
                    <td key={k} style={{ padding: '0.5rem', textAlign: 'right' }}>
                      {formatMoney(ratePerArea(measuredQuoted, grandAreaM2(k)), cur)}
                      {mode === 'zone' && <span style={{ fontWeight: 400, fontSize: '0.72rem', color: 'var(--brio-text-muted)' }}> wtd avg</span>}
                    </td>
                  ))}
                </tr>
                {lumpItems.length > 0 && (
                  <tr style={{ color: 'var(--brio-text-muted)' }}>
                    <td style={{ padding: '0.5rem' }}>Provisional sums & PC items</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatMoney(provisionalQuoted, cur)}</td>
                    <td />
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatMoney(provisionalBilled, cur)}</td>
                    {activeKeys.map((k) => <td key={k} />)}
                  </tr>
                )}
                <tr style={{ borderTop: '2px solid var(--brio-border, #ddd)', fontWeight: 700 }}>
                  <td style={{ padding: '0.5rem' }}>Grand total</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatMoney(grandQuoted, cur)}</td>
                  <td />
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatMoney(grandBilled, cur)}</td>
                  {activeKeys.map((k) => <td key={k} />)}
                </tr>
              </tfoot>
            </table>
            {availableKeys.length === 0 && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--brio-text-muted)' }}>
                {mode === 'zone'
                  ? 'Add areas to zones (Edit on a zone in the Bill tab) to see cost per m² by zone.'
                  : 'Set a built-up / carpet / construction area in “Edit BOQ details” to see rate per area.'}
              </div>
            )}
            {activeKeys.length > 0 && (
              <div style={{ marginTop: '0.4rem', fontSize: '0.75rem', color: 'var(--brio-text-muted)' }}>
                Rate-per-area columns use the estimate amount.{mode === 'zone' ? ' Each zone is divided by its own area; the grand total is the area-weighted average.' : ''}{mode === 'trade' ? ' Click a sub-trade (▸) to expand its individual line items.' : ''}{hasSubRows ? ` The PDF exports ${exportDetail === 'headers' ? 'the chapter headers only' : 'the chapter headers plus the sub-trade rows'} — both foot to the same grand total.` : ''}
              </div>
            )}
          </StickyScrollBox>
        )}
      </BrioCard>

      <BuildUpCard projApi={adjApi} netQuoted={grandQuoted} netBilled={grandBilled} currency={cur} />

      <BrioCard>
        <h3 style={{ marginTop: 0, fontFamily: 'Raleway, sans-serif', fontSize: '1.05rem' }}>Inclusions, exclusions & assumptions</h3>
        <p style={{ fontSize: '0.82rem', color: 'var(--brio-text-muted)', marginTop: 0 }}>
          These print on the bill / summary PDF cover. Click a dashed chip to add a standard clause from the library, or “+ Custom”. Edit text inline.
        </p>
        {clausesApi.error && <div style={{ color: '#a94442' }}>{clausesApi.error}</div>}
        {CLAUSE_KINDS.map((kind) => (
          <ClauseSection
            key={kind}
            kind={kind}
            clauses={clausesApi.clauses}
            library={clausesApi.library}
            onAdd={(k) => clausesApi.addClause({ kind: k, text: 'New clause — edit me' })}
            onAddFromLibrary={(s) => clausesApi.addClause({ kind: s.kind, text: s.text, sourceClauseId: s.id })}
            onUpdate={clausesApi.updateClause}
            onDelete={clausesApi.deleteClause}
          />
        ))}
      </BrioCard>
    </div>
  );
}

export default SummaryTab;
